import axios from "axios";
import RestClient from "RestClient";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const restClient = new RestClient();

/**
 * SECURITY: CWE-294 - Response manipulation mitigation.
 * Validates that the token from the login response is accepted by the server.
 * Call this before trusting the login response and setting session.
 * @param {string} token - JWT from login response
 * @returns {Promise<{ valid: boolean }>}
 */
async function validateSessionWithToken(token) {
  try {
    const response = await axios.get(`${BASE_URL}/users/current`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    return { valid: response?.status === 200 };
  } catch (err) {
    if (err?.response?.status === 401) {
      return { valid: false };
    }
    return { valid: false };
  }
}

/**
 * Fetch current user from server using token.
 * SECURITY: Do not trust mutable login response profile fields.
 * Always derive user identity attributes from this endpoint.
 * @param {string} token - JWT from login response
 * @returns {Promise<Object|null>}
 */
async function fetchCurrentUserWithToken(token) {
  try {
    const response = await axios.get(`${BASE_URL}/users/current`, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    return response?.data || null;
  } catch (err) {
    return null;
  }
}

/**
 * CWE-319: Encrypt password with RSA-OAEP using server's public key so it is not sent in cleartext.
 * Uses Web Crypto API (no extra dependency). Returns base64-encoded ciphertext.
 */
async function encryptPasswordWithPublicKey(publicKeyPem, password) {
  const pemContents = publicKeyPem
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s/g, "");
  const binaryDer = atob(pemContents);
  const bytes = new Uint8Array(binaryDer.length);
  for (let i = 0; i < binaryDer.length; i++) {
    bytes[i] = binaryDer.charCodeAt(i);
  }
  const publicKey = await crypto.subtle.importKey(
    "spki",
    bytes.buffer,
    { name: "RSA-OAEP", hash: "SHA-1" },
    false,
    ["encrypt"],
  );
  const encoded = new TextEncoder().encode(password);
  const encrypted = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    publicKey,
    encoded,
  );
  const encryptedArray = new Uint8Array(encrypted);
  let binary = "";
  for (let i = 0; i < encryptedArray.length; i++) {
    binary += String.fromCharCode(encryptedArray[i]);
  }
  return btoa(binary);
}

/**
 * Fetches the login public key for client-side password encryption (CWE-319).
 */
export const getLoginPublicKey = async () => {
  const result = await restClient.get(
    `${BASE_URL}/users/login-public-key`,
    {},
    { skipAuthRedirect: true },
  );
  if (result?.status === false) {
    return result;
  }
  const data = result?.data ?? result;
  return data?.publicKey
    ? {
        status: true,
        publicKey: data.publicKey,
        challengeId: data.challengeId,
        challengeToken: data.challengeToken,
      }
    : { status: false };
};

/**
 * All known session storage keys used by the application.
 * SECURITY: Proper session termination - clear every one of these on logout.
 */
const SESSION_STORAGE_KEYS = [
  "user",
  "userInfo",
  "userSettings",
  "brokerInfo",
  "agentInfo",
  "clientSettings",
  "clientInfo",
  "csrfToken",
];

export const emailValidation = async (payload) => {
  const result = await restClient.get(
    `${BASE_URL}/users/validateEmail`,
    payload,
  );
  return result;
};

/**
 * CWE-319: Login with password encrypted client-side so it never appears in cleartext in the payload.
 * Fetches the server's public key, encrypts the password, and sends encryptedPassword instead of password.
 */
export const doLogin = async (payload) => {
  const loginPayload = { ...payload };
  if (loginPayload.password) {
    try {
      const keyResult = await getLoginPublicKey();
      if (!keyResult?.publicKey) {
        return {
          status: false,
          message: "Unable to get encryption key. Please try again.",
        };
      }
      if (!keyResult?.challengeId || !keyResult?.challengeToken) {
        return {
          status: false,
          message: "Unable to validate login challenge. Please try again.",
        };
      }
      const challengeBoundPassword = `${keyResult.challengeId}::${loginPayload.password}`;
      loginPayload.encryptedPassword = await encryptPasswordWithPublicKey(
        keyResult.publicKey,
        challengeBoundPassword,
      );
      loginPayload.challengeId = keyResult.challengeId;
      loginPayload.challengeToken = keyResult.challengeToken;
      delete loginPayload.password;
    } catch (err) {
      return {
        status: false,
        message: "Unable to secure password. Please try again.",
      };
    }
  }
  const result = await restClient.post(
    `${BASE_URL}/users/authenticate`,
    loginPayload,
  );
  const { data, status } = result;
  if (status) {
    // SECURITY: CWE-294 - Do not trust login response alone. Validate token with server.
    const token = data?.result?.token;
    if (token) {
      const { valid } = await validateSessionWithToken(token);
      if (!valid) {
        return {
          status: false,
          message: "Session validation failed. Please try again.",
        };
      }

      // SECURITY: Replace login response userInfo with server-verified current user.
      // This blocks privilege escalation via response manipulation in intercepted login responses.
      const trustedCurrentUser = await fetchCurrentUserWithToken(token);
      if (!trustedCurrentUser) {
        return {
          status: false,
          message: "Session validation failed. Please try again.",
        };
      }
      if (data?.result) {
        data.result.userInfo = trustedCurrentUser;
      }
    }
    return data;
  } else {
    return result;
  }
};

/**
 * Existing-user verification (no CAPTCHA) for migration flow.
 * Uses the same encrypted-password mechanism as doLogin.
 */
export const verifyExistingUser = async (payload) => {
  const verifyPayload = { ...payload };
  if (verifyPayload.password) {
    try {
      const keyResult = await getLoginPublicKey();
      if (!keyResult?.publicKey) {
        return { status: false, message: "Unable to get encryption key. Please try again." };
      }
      if (!keyResult?.challengeId || !keyResult?.challengeToken) {
        return { status: false, message: "Unable to validate login challenge. Please try again." };
      }
      const challengeBoundPassword = `${keyResult.challengeId}::${verifyPayload.password}`;
      verifyPayload.encryptedPassword = await encryptPasswordWithPublicKey(
        keyResult.publicKey,
        challengeBoundPassword,
      );
      verifyPayload.challengeId = keyResult.challengeId;
      verifyPayload.challengeToken = keyResult.challengeToken;
      delete verifyPayload.password;
    } catch {
      return { status: false, message: "Unable to secure password. Please try again." };
    }
  }
  const result = await restClient.post(`${BASE_URL}/users/verifyExistingUser`, verifyPayload);
  const { data, status } = result;
  if (status) return data;
  return result;
};

export const updateMobileNumber = async (mobile_number) => {
  const result = await restClient.post(`${BASE_URL}/users/updateMobileNumber`, { mobile_number });
  const { data, status } = result;
  if (status) return data;
  return result;
};

/**
 * Update user record via /users/:id (partial updates allowed).
 * Prefer this over /profile/:id which requires zipcode/city/state/country/address.
 */
export const updateUserById = async (id, payload) => {
  const result = await restClient.put(`${BASE_URL}/users/${id}`, payload);
  const { data, status } = result;
  if (status) return data;
  return result;
};

/**
 * SECURITY: CWE-645 - Missing Brute Force Protection - Mitigation
 *
 * Fetches a CAPTCHA challenge from the server.
 * Used when CAPTCHA is required after multiple failed login attempts.
 */
export const getCaptcha = async () => {
  const result = await restClient.get(`${BASE_URL}/users/captcha`);
  const { data, status } = result;
  if (status) {
    return data;
  } else {
    return result;
  }
};

/**
 * SECURITY: CWE-645 - Missing Brute Force Protection - Mitigation
 *
 * Checks if CAPTCHA is required for a given email address.
 * Allows the frontend to proactively show CAPTCHA.
 */
export const checkCaptchaRequired = async (email) => {
  const result = await restClient.get(
    `${BASE_URL}/users/checkCaptchaRequired`,
    { email },
  );
  const { data, status } = result;
  if (status) {
    return data;
  } else {
    return result;
  }
};

export const updatePassword = async (payload) => {
  const result = await restClient.post(
    `${BASE_URL}/users/resetpassword`,
    payload,
  );
  const { data, status } = result;
  if (status) {
    return data;
  } else {
    return result;
  }
};

export const doRegister = async (payload) => {
  const result = await restClient.post(`${BASE_URL}/users/register`, payload);
  return result;
};

/**
 * Checks if a certificate registration number belongs to an already-registered (old) user.
 * Requires auth; used by "Already Registered" onboarding.
 */
export const checkOldRegistrationNumber = async (regNumber) => {
  const result = await restClient.get(`${BASE_URL}/users/checkOldRegistrationNumber`, { regNumber });
  const { data, status } = result;
  if (status) return data;
  return result;
};

export const checkIfLoggedIn = () => {
  const result =
    sessionStorage.getItem("user") || sessionStorage.getItem("userInfo");
  return result || false;
};

/**
 * Logout function – proper session termination
 * SECURITY: Destroy session by (1) invalidating token on backend, (2) clearing session timeout,
 * (3) deleting all session variables, (4) clearing entire sessionStorage.
 */
export const doLogout = async () => {
  try {
    // SECURITY: Call backend logout endpoint to invalidate token server-side
    // This prevents replay attacks using logged-out tokens
    // App stores user in "user" (setSessionUserInformation), not "userInfo"
    const userInfo = JSON.parse(sessionStorage.getItem("user") || "{}");
    if (userInfo?.token) {
      try {
        // skipAuthRedirect: avoid RestClient redirect on 401; we clear storage and navigate in Header
        await restClient.post(`${BASE_URL}/users/logout`, {}, true, {
          skipAuthRedirect: true,
        });
      } catch (error) {
        // Even if backend call fails, clear client-side storage
        // This ensures logout works even if backend is unreachable
        console.error("Logout API call failed, clearing local storage:", error);
      }
    }

    // SECURITY: Delete every known session variable explicitly, then clear all
    SESSION_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
    sessionStorage.clear();
  } catch (error) {
    // Ensure session is destroyed even if something above throws
    try {
      SESSION_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
      sessionStorage.clear();
    } catch (e) {
      sessionStorage.clear();
    }
  }
  return true;
};

export const requestResetPassword = async (payload) => {
  const result = await restClient.post(
    `${BASE_URL}/users/requestPasswordReset`,
    payload,
  );
  return result;
};

export const doResetPassword = async (payload) => {
  const result = await restClient.post(
    `${BASE_URL}/users/doPasswordReset`,
    payload,
    true,
    { skipAuthRedirect: true },
  );
  return result;
};

export const fetchCategories = async () => {
  const result = await restClient.get(`${BASE_URL}/categories`);
  return result;
};

export const doPayment = async (payload) => {
  const result = await restClient.post(`${BASE_URL}/payments`, payload);
  return result;
};

export const verifyPayment = async (payload) => {
  // Prefer GET so the verify request is never sent to `/` as a POST.
  // Backend should accept query params for verification.
  const getResult = await restClient.get(`${BASE_URL}/payments/verify`, payload);

  // Backward compatibility: if backend only supports POST, fall back.
  if (getResult?.status === false) {
    const postResult = await restClient.post(`${BASE_URL}/payments/verify`, payload);
    return postResult;
  }

  return getResult;
};

export const insertSettings = async (payload) => {
  const result = await restClient.post(`${BASE_URL}/settings`, payload);
  return result;
};

/**
 * SECURITY: CWE-840 - Business Logic Errors - Mitigation
 *
 * Validates workflow step access server-side.
 * Prevents users from accessing steps out of sequence.
 */
export const validateWorkflowStep = async (stepIndex) => {
  const result = await restClient.get(
    `${BASE_URL}/users/workflow/validateStep`,
    { stepIndex },
  );
  const { data, status } = result;
  if (status) {
    return data;
  } else {
    return result;
  }
};

/**
 * SECURITY: CWE-840 - Business Logic Errors - Mitigation
 *
 * Gets workflow step status for the current user.
 */
export const getWorkflowStepStatus = async () => {
  const result = await restClient.get(`${BASE_URL}/users/workflow/stepStatus`);
  const { data, status } = result;
  if (status) {
    return data;
  } else {
    return result;
  }
};

/**
 * SECURITY: CWE-840 - Business Logic Errors - Mitigation
 *
 * Validates a password reset token before showing the reset form.
 */
export const validateResetToken = async (email, token) => {
  const result = await restClient.get(
    `${BASE_URL}/users/validateResetToken`,
    { email, token },
    { skipAuthRedirect: true },
  );
  const { data, status } = result;
  if (status) {
    return data;
  } else {
    return result;
  }
};
